import { ELocalStorageKeys } from 'constants/enums';

const CONSENT_COOKIE_NAME = 'analyticsConsent';
const LEGACY_LOCAL_STORAGE_KEY = ELocalStorageKeys.ANALYTICS;
const MAX_AGE_DAYS = 400;
const SECONDS_PER_DAY = 24 * 60 * 60;

export const normalizeConsent = (value: unknown): boolean | null => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'boolean') return value;
  if (value === 'true' || value === '"true"') return true;
  if (value === 'false' || value === '"false"') return false;
  return null;
};

export const resolveConsentCookieDomain = (hostname?: string): string | undefined => {
  if (!hostname) return undefined;
  if (hostname === 'localhost') return undefined;
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(hostname)) return undefined;
  const labels = hostname.split('.');
  if (labels.length < 2) return undefined;
  return `.${labels.slice(-2).join('.')}`;
};

const resolveDomain = (): string | undefined => {
  const configured = process.env.REACT_APP_CONSENT_COOKIE_DOMAIN;
  if (configured) return configured;
  if (typeof window === 'undefined') return undefined;
  return resolveConsentCookieDomain(window.location.hostname);
};

const readCookie = (name: string): string | null => {
  if (typeof document === 'undefined') return null;
  const entry = document.cookie.split('; ').find((row) => row.startsWith(`${name}=`));
  if (entry === undefined) return null;
  return decodeURIComponent(entry.slice(name.length + 1));
};

const buildCookie = (
  name: string,
  value: string,
  maxAgeSeconds: number,
  domain?: string
): string => {
  const parts = [
    `${name}=${encodeURIComponent(value)}`,
    'path=/',
    `max-age=${maxAgeSeconds}`,
    'SameSite=Lax',
  ];
  if (domain) {
    parts.push(`domain=${domain}`);
  }
  if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
    parts.push('Secure');
  }
  return parts.join('; ');
};

const writeCookie = (name: string, value: string, maxAgeDays: number, domain?: string): void => {
  if (typeof document === 'undefined') return;
  document.cookie = buildCookie(name, value, maxAgeDays * SECONDS_PER_DAY, domain);
};

const removeCookie = (name: string, domain?: string): void => {
  if (typeof document === 'undefined') return;
  document.cookie = buildCookie(name, '', 0, domain);
};

const readLegacyLocalStorage = (): boolean | null => {
  if (typeof window === 'undefined') return null;
  try {
    return normalizeConsent(window.localStorage.getItem(LEGACY_LOCAL_STORAGE_KEY));
  } catch {
    return null;
  }
};

const resolveConsent = (): boolean | null => {
  const fromCookie = normalizeConsent(readCookie(CONSENT_COOKIE_NAME));
  if (fromCookie !== null) return fromCookie;

  const fromLegacy = readLegacyLocalStorage();
  if (fromLegacy !== null) {
    writeCookie(CONSENT_COOKIE_NAME, String(fromLegacy), MAX_AGE_DAYS, resolveDomain());
    return fromLegacy;
  }

  return null;
};

export const getAnalyticsConsent = (): boolean | null => resolveConsent();

export const setAnalyticsConsent = (granted: boolean): void => {
  writeCookie(CONSENT_COOKIE_NAME, String(granted), MAX_AGE_DAYS, resolveDomain());
};

export const clearAnalyticsConsent = (): void => {
  removeCookie(CONSENT_COOKIE_NAME, resolveDomain());
};
